import { Box, Stack, Typography } from '@ds/coolshop';
import { BaseTable, SearchFilterOperator } from '@ds/data-table';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { getTablePaginationRowsLabel } from '@src/utils/form/shared';
import { ColumnFilter, ColumnSort, PaginationState } from '@tanstack/react-table';
import _ from 'lodash';
import { ReactElement, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { DETAIL_TYPE_LABELS } from '../../../registry';
import { Detail, DetailType, JobResultView } from '../../../types';
import { detailTypes } from '../JobData.helpers';

/**
 * Normalizes a cell value for display, filtering and sorting. Null and undefined are real:
 * detailTypes writes `change: null` for several detail types, and old job documents simply
 * lack fields — calling .toString() on those threw and took down the table.
 */
const toDisplayValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

interface IJobInfoDetailsProps {
    job: JobResultView;
    detailType: DetailType;
}

type Row = Detail | { code: string };

export function JobDetails(props: Readonly<IJobInfoDetailsProps>) {
    const { job, detailType } = props;

    const intl = useIntl();
    const { searchDataPagination } = useContext(SettingsContext);
    const detailLabelDescriptor = DETAIL_TYPE_LABELS[detailType];
    const detailLabel = detailLabelDescriptor
        ? intl.formatMessage(detailLabelDescriptor)
        : detailType;

    const { descriptionComponent, details: initialDetails } = useMemo(
        () => detailTypes(job, detailType),
        [detailType],
    );

    const details: Row[] = useMemo(
        () => initialDetails.map(d => (typeof d === 'string' ? { code: d } : d)),
        [initialDetails],
    );

    const [size, setSize] = useState(10);
    const [rows, setRows] = useState<({ code: string } | Detail)[]>(details.slice(0, size));
    const [count, setCount] = useState(details.length);
    const [filters, setFilters] = useState<ColumnFilter[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const columns = useMemo(() => {
        const excludeReplacement = rows.every(
            r =>
                (typeof r === 'object' && !('isReplacement' in r)) ||
                !r?.isReplacement ||
                [DetailType.ALIGNED_GLS, DetailType.ALIGNED_MODELS].includes(detailType),
        );
        const firstRow = (details[0] || {}) as Record<string, unknown>;
        const isRenderable = (value: unknown) =>
            value === null ||
            value === undefined ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean';
        const cols = Object.keys(firstRow).reduce<string[]>((acc, key) => {
            if (
                key !== 'hierarchyId' &&
                // Only there to route the row click to the prototypes page.
                key !== 'prototypeModelId' &&
                (key !== 'isReplacement' || !excludeReplacement) &&
                key !== 'changesWithType' &&
                isRenderable(firstRow[key])
            ) {
                acc.push(key);
            }
            return acc;
        }, []);

        return [
            {
                // The id keeps deriving from the enum value: the data-table persists column
                // state by id, so it must not change when the label is translated.
                id: _.camelCase(detailType),
                header: detailLabel,
                columns: cols.map(key => ({
                    id: `${_.camelCase(detailType)}-${key}`,
                    header: _.startCase(key),
                    accessorKey: key,
                    enableColumnFilter: true,
                    // Declared explicitly so every column gets a free-text search: left to
                    // infer, the data-table renders a numeric filter for numeric values.
                    meta: {
                        filter: {
                            contentType: 'text',
                            type: 'text' as const,
                            operator: SearchFilterOperator.Contains,
                        },
                    },
                    ...(detailType === DetailType.FAILURES ? { maxWidth: false } : {}),
                    // eslint-disable-next-line react/no-unstable-nested-components
                    cell: (params: { row: { original: Row } }) =>
                        params.row.original[key as keyof Row] as unknown as ReactElement,
                })),
            },
        ];
    }, [details, detailType, detailLabel, rows]);

    const processFilters = (startingRows: Row[], filtersState: ColumnFilter[]) => {
        if (filtersState.length) {
            return startingRows
                .filter(r => {
                    if (
                        filtersState.every(filter => {
                            const filterValue = toDisplayValue(
                                (filter.value as { value?: unknown })?.value,
                            );
                            // Column ids are `${camelCase(detailType)}-${key}`; a key can
                            // itself contain a dash, so keep everything after the first one.
                            const filterId = filter.id.split('-').slice(1).join('-');
                            const rowValue = toDisplayValue(r[filterId as keyof Row]);
                            if (
                                filterValue &&
                                rowValue &&
                                !rowValue.toLowerCase().includes(filterValue.toLowerCase())
                            ) {
                                return false;
                            }
                            return true;
                        })
                    ) {
                        return r;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        return startingRows;
    };

    const onFiltersChange = (filtersState: ColumnFilter[]) => {
        setFilters(filtersState);
        const filteredRows = processFilters(details, filtersState);
        const newRows = filteredRows.slice(0, size);
        setHasNextPage(newRows.length === size);
        setRows(newRows);
        setCount(filteredRows.length);
    };

    const onSortingChange = (
        sortingState: (ColumnSort & {
            type?: string;
        })[],
    ) => {
        if (sortingState.length) {
            const filteredRows = processFilters(details, filters);
            const sortedRows = [...filteredRows].sort((a, b) => {
                const sort = sortingState[0];
                // Same id shape as the filters: strip the detail-type prefix to get the key.
                const sortKey = sort.id.split('-').slice(1).join('-') as keyof Row;
                const aValue = toDisplayValue(a[sortKey]);
                const bValue = toDisplayValue(b[sortKey]);
                if (sort.desc) {
                    return aValue > bValue ? -1 : 1;
                }
                return aValue > bValue ? 1 : -1;
            });
            const newRows = sortedRows.slice(0, size);
            setRows(newRows);
        }
    };

    const onPaginationChange = (paginationState: PaginationState) => {
        const { pageIndex, pageSize } = paginationState;
        const filteredRows = processFilters(details, filters);
        const newRows = filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
        setHasNextPage(newRows.length === pageSize);
        setRows(newRows);
        setSize(pageSize);
        setCurrentPage(pageIndex);
    };

    const onRowClick = (row: any) => {
        // Prototypes have their own page, addressed by the CGT model they belong to: rows carrying
        // a model id open there, the generic node editor would show a form nobody edits.
        if (
            row.original &&
            typeof row.original === 'object' &&
            'prototypeModelId' in row.original &&
            row.original.prototypeModelId
        ) {
            window.open(
                `/prototypes/${btoa(encodeURIComponent(row.original.prototypeModelId))}`,
                '_blank',
            );
            return;
        }
        if (
            row.original &&
            typeof row.original === 'object' &&
            'hierarchyId' in row.original &&
            'nodeId' in row.original &&
            row.original.hierarchyId &&
            row.original.nodeId
        ) {
            window.open(
                `/hierarchies/${btoa(encodeURIComponent(row.original.hierarchyId))}/${btoa(
                    encodeURIComponent(row.original.nodeId),
                )}`,
                '_blank',
            );
        }
    };

    return (
        <Stack gap={2} width="100%" flex={1} minHeight={480}>
            <Typography color="primary" variant="h6">
                <b>{detailLabel}</b>
            </Typography>
            <Typography>{descriptionComponent}</Typography>
            {!!details.length && (
                <Box flex={1} minHeight={0} display="flex">
                    <BaseTable<Row>
                        id="job-details"
                        columns={columns}
                        data={rows}
                        count={count}
                        selectedRows={[]}
                        initialFilters={[]}
                        disabledFilters={[]}
                        manualPagination
                        hasNextPage={hasNextPage}
                        hasPreviousPage={currentPage > 0}
                        getTablePaginationRowsLabel={() =>
                            getTablePaginationRowsLabel(size, currentPage, count)
                        }
                        onRowClick={onRowClick}
                        handleStateChange={({
                            pagination: paginationState,
                            sorting: sortingState,
                            filters: filtersState,
                        }) => {
                            if (filtersState) {
                                onFiltersChange(filtersState);
                            }
                            if (sortingState) {
                                onSortingChange(sortingState);
                            }
                            if (paginationState) {
                                onPaginationChange(paginationState);
                            }
                        }}
                        settings={{
                            pagination: searchDataPagination || undefined,
                            // No "show only empty rows" toggle: these tables are
                            // searched by value, not by emptiness.
                            showColumnFilterCheckbox: false,
                        }}
                        excludeDndProvider
                    />
                </Box>
            )}
        </Stack>
    );
}
